/**
 * Analytics Event Keys
 *
 * This file contains the event keys for the Procore analytics system.
 *
 * ## Key Naming Convention
 *
 * Keys follow the format: `scope.tool.object.action`
 * - **scope**: The level of the event (e.g., `project`, `company`)
 * - **tool**: The product/feature area (e.g., `3d_models`)
 * - **object**: The component being interacted with (e.g., `bim_toolbar`, `bim_viewer`)
 * - **action**: The user action (e.g., `tapped`, `loaded`, `searched`)
 *
 * ## Resources
 *
 * - **Key Guidelines**: https://procoretech.atlassian.net/wiki/spaces/METRIC/pages/168009641/Overview+of+Analytics+Keys
 * - **Configuring Analytic Keys**: https://procoretech.atlassian.net/wiki/spaces/METRIC/pages/344686902/Configuring+Analytic+Keys
 * - **Amplitude Key Catalog**: https://procoretech.atlassian.net/wiki/spaces/BIM/pages/4783276143/Amplitude+Key+Catalog
 *
 * ## When Adding New Events
 *
 * 1. Follow the naming convention above
 * 2. Check the Amplitude Key Catalog to see if a key already exists
 * 3. If reusing an existing key for a different platform, update the "Application" column in the catalog
 *    to differentiate where the event originated (important for cross-platform analytics)
 * 4. If adding a new key, add the key to the Amplitude Key Catalog and configure the key in the Procore repo.
 * 5. Add the key to this file for type safety and autocomplete
 *
 */

/** How the GPU guidance steps checklist was opened. */
export const WizardTrigger = {
    Auto: "auto",
    Troubleshoot: "troubleshoot",
    HardStop: "hard-stop",
} as const;
export type WizardTrigger = (typeof WizardTrigger)[keyof typeof WizardTrigger];

/** Where the user action was triggered from. */
export const EventOrigin = {
    Toolbar: "toolbar",
    Keyboard: "keyboard",
    ContextMenu: "context_menu",
    ObjectTree: "object_tree",
    PropertiesPanel: "properties_panel",
    SelectionBar: "selection_bar",
    SpaceMouse: "space_mouse",
} as const;
export type EventOrigin = (typeof EventOrigin)[keyof typeof EventOrigin];

/** Active mode when a measurement tool is enabled. */
export const MeasureMode = {
    Laser: "laser",
    Coordinates: "coordinates",
    PointToPoint: "point_to_point",
    Dimension: "dimension",
    ShortestDistance: "shortest_distance",
} as const;
export type MeasureMode = (typeof MeasureMode)[keyof typeof MeasureMode];

/** Active mode when a section tool is enabled. */
export const SectionMode = {
    ClipPlane: "clip_plane",
    Box: "box",
} as const;
export type SectionMode = (typeof SectionMode)[keyof typeof SectionMode];

export const Events = {
    /**
     * Top-level viewer actions grouped under the `bim_toolbar` feature namespace.
     *
     * Note: despite the `bim_toolbar` segment in the key name, these events are not limited
     * to toolbar button clicks. The namespace reflects the feature group, not the input method.
     * Always pass `origin` to indicate how the action was triggered.
     *
     * @example
     * // track(Events.toolbar.homeTapped, { origin: EventOrigin.Toolbar });
     * // track(Events.toolbar.homeTapped, { origin: EventOrigin.Keyboard });
     */
    toolbar: {
        homeTapped: "project.3d_models.bim_toolbar.home_tapped",
        settingsTapped: "project.3d_models.bim_toolbar.settings_tapped",
        objectTreeTapped: "project.3d_models.bim_toolbar.object_tree_tapped",
        propertiesPanelTapped: "project.3d_models.bim_toolbar.properties_panel_tapped",
        measureTapped: "project.3d_models.bim_toolbar.measure_tapped",
        sectionTapped: "project.3d_models.bim_toolbar.section_tapped",
        cameraTapped: "project.3d_models.bim_toolbar.camera_tapped",
        undoTapped: "project.3d_models.bim_toolbar.undo_tapped",
        redoTapped: "project.3d_models.bim_toolbar.redo_tapped",
        resetTapped: "project.3d_models.bim_toolbar.reset_tapped",
        navigationDefaultTapped: "project.3d_models.bim_toolbar.navigation_default_tapped",
        navigationOrbitTapped: "project.3d_models.bim_toolbar.navigation_orbit_tapped",
        navigationFlyTapped: "project.3d_models.bim_toolbar.navigation_fly_tapped",
    },
    /** Fired when the BIM model is first opened in the viewer. */
    bimModel: {
        opened: "project.3d_models.bim_model.opened",
    },
    /**
     * Fired when the user clicks a face on the axis sphere to reorient the camera.
     * @example
     * // track(Events.axisSphere.faceTapped, { part_name: "part_front" });
     * // track(Events.axisSphere.faceTapped, { part_name: "part_top_front_left" });
     */
    axisSphere: {
        faceTapped: "project.3d_models.axis_sphere.face_tapped",
    },
    /**
     * Fired when a measurement mode is activated.
     * @example
     * // track(Events.measure.enabled, { mode: MeasureMode.Laser, origin: EventOrigin.Toolbar });
     * // track(Events.measure.enabled, { mode: MeasureMode.PointToPoint, origin: EventOrigin.Keyboard });
     */
    measure: {
        enabled: "project.3d_models.measure.enabled",
    },
    /**
     * Fired when a section tool is activated.
     * @example
     * // track(Events.clipPlane.enabled, { mode: SectionMode.ClipPlane, origin: EventOrigin.Toolbar });
     * // track(Events.clipPlane.enabled, { mode: SectionMode.ClipPlane, origin: EventOrigin.ContextMenu });
     */
    clipPlane: {
        enabled: "project.3d_models.clip_plane.enabled",
    },
    /**
     * Object-level actions. Always include `origin` to identify the triggering surface.
     * @example
     * // track(Events.bimObjects.hidden, { origin: EventOrigin.ContextMenu });
     * // track(Events.bimObjects.isolated, { origin: EventOrigin.ObjectTree });
     * // track(Events.bimObjects.zoomedToSelection, { origin: EventOrigin.Keyboard });
     */
    bimObjects: {
        hidden: "project.3d_models.bim_objects.hide",
        shown: "project.3d_models.bim_objects.show",
        hideSimilar: "project.3d_models.bim_objects.hide_similar",
        selectSimilar: "project.3d_models.bim_objects.select_similar",
        isolated: "project.3d_models.bim_objects.isolate",
        isolatedInXray: "project.3d_models.bim_objects.isolate_in_xray",
        unisolated: "project.3d_models.bim_objects.unisolate",
        unisolatedFromXray: "project.3d_models.bim_objects.unisolate_from_xray",
        propertiesShown: "project.3d_models.bim_objects.show_properties",
        zoomedToSelection: "project.3d_models.bim_objects.zoom_to_selection",
    },
    /** Object Tree panel actions */
    objectTree: {
        searched: "project.3d_models.bim_object_tree.searched",
        hideAll: "project.3d_models.bim_object_tree.hide_all",
        selectAll: "project.3d_models.bim_object_tree.select_all",
        collapseAll: "project.3d_models.bim_object_tree.collapse_all",
    },
    /** Properties panel actions */
    propertyPanel: {
        searched: "project.3d_models.bim_properties.searched",
    },
    /** Authentication events */
    auth: {
        failed: "project.3d_models.bim_auth.failed",
    },
    /** Viewpoint events */
    viewpoint: {
        created: "project.3d_models.bim_viewer.viewpoint_created",
        restored: "project.3d_models.bim_viewer.viewpoint_restored",
    },
    /**
     * Viewer stability events.
     *
     * @example
     * // trackBeacon(Events.viewer.crashed, { reason: "webgl_context_lost", platform: "android" });
     */
    viewer: {
        /**
         * Fired when the 3D context is lost — the strongest in-page precursor to a
         * viewer crash. Sent via `trackBeacon` because the page often dies immediately after.
         * Honeycomb also records `page_frozen` and `abnormal_exit`; those stay off Amplitude
         * until they are proven not to be dominated by force-quit / app-switcher noise.
         */
        crashed: "project.3d_models.bim_viewer.crashed",
    },
    /** Scene error screen events */
    sceneError: {
        /** Fired once when the SceneError screen is first shown to the user. */
        viewed: "project.3d_models.bim_viewer.scene_error_viewed",
        /** Fired when the user clicks the "Switch User / Refresh" retry button. */
        retry: "project.3d_models.bim_viewer.scene_error_retry",
        /** Fired via `beforeunload` when the user closes or navigates away from the error screen. */
        abandon: "project.3d_models.bim_viewer.scene_error_abandon",
    },
    /**
     * GPU capability classification, emitted on the first guidance snapshot and
     * again whenever the situation or degradation mode changes. Fix rate is read by
     * stitching a user's sessions on this event: a later `healthy` after a
     * non-healthy one is the fix. There is deliberately no "fixed" event — a
     * GPU-preference change still classifies as `integrated-gpu` on a same-process
     * reload; only a full browser quit-and-reopen can look healthy.
     *
     * @example
     * // track(Events.gpuDetection.classified, { situation: "integrated-gpu", renderer, ... });
     */
    gpuDetection: {
        classified: "project.3d_models.gpu_detection.classified",
    },
    /**
     * GPU guidance wizard funnel. Always pass `trigger` on `shown` to distinguish a
     * passive nudge from a deliberate Troubleshoot / Get Started click.
     *
     * @example
     * // track(Events.gpuWizard.shown, { trigger: WizardTrigger.Troubleshoot, situation: "integrated-gpu" });
     */
    gpuWizard: {
        shown: "project.3d_models.gpu_wizard.shown",
        dismissed: "project.3d_models.gpu_wizard.dismissed",
        linkClicked: "project.3d_models.gpu_wizard.link_clicked",
    },
    /**
     * SpaceMouse adoption. `connected` and `navigated` fire once per live session, not per
     * reconnect and not per motion sample. Command use is one event per invocation, keyed by
     * the stable command id rather than the localized label.
     *
     * @example
     * // track(Events.spaceMouse.connected);
     * // track(Events.spaceMouse.navigated);
     * // track(Events.spaceMouse.commandInvoked, { command: "ID_HIDE" });
     */
    spaceMouse: {
        connected: "project.3d_models.space_mouse.connected",
        navigated: "project.3d_models.space_mouse.navigated",
        commandInvoked: "project.3d_models.space_mouse.command_invoked",
    },
} as const;
